import { Link } from "react-router-dom";
import { useEffect } from "react";
import { LEGAL } from "@/constants/testIds";
import { ArrowLeft } from "lucide-react";

export const LegalPage = ({ title, lastUpdated, children }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    document.title = `${title} — NAIJA Cashews`;
  }, [title]);

  return (
    <div
      data-testid={LEGAL.wrapper}
      className="min-h-screen bg-[#faf7f2] text-[#1a3626]"
    >
      <header className="border-b border-[#1a3626]/8 bg-[#faf7f2]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link
            data-testid={LEGAL.back}
            to="/"
            className="inline-flex items-center gap-2 text-[13px] text-[#1a3626]/75 hover:text-[#1a3626]"
          >
            <ArrowLeft size={15} /> Back to home
          </Link>
          <span className="font-serif-display text-[18px]">
            NAIJA <span className="text-[#8b5a2b]">Cashews</span>
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="overline">Legal</div>
        <h1 className="mt-4 font-serif-display text-[40px] sm:text-[56px] leading-[1.05] tracking-tight">
          {title}
        </h1>
        {lastUpdated && (
          <div className="mt-4 text-[12px] uppercase tracking-[0.22em] text-[#8b5a2b]">
            Last updated · {lastUpdated}
          </div>
        )}
        <div className="mt-10 gold-hairline" />
        <article className="mt-10 prose-naija text-[15.5px] leading-[1.85] text-[#5c4033] space-y-6">
          {children}
        </article>
      </main>
    </div>
  );
};

const H = ({ children }) => (
  <h2 className="font-serif-display text-[24px] sm:text-[28px] text-[#1a3626] tracking-tight mt-10 first:mt-0">
    {children}
  </h2>
);

export const Impressum = () => (
  <LegalPage title="Impressum">
    <H>Angaben gemäß § 5 TMG</H>
    <p>
      <span className="font-medium text-[#1a3626]">NAIJA Cashews</span>
      <br />
      Einzelunternehmen
    </p>

    <H>Inhaber</H>
    <p>Neal Chidi Emegwele</p>

    <H>Anschrift</H>
    <p>
      Loewenhardtdamm 34
      <br />
      12101 Berlin
      <br />
      Deutschland
    </p>

    <H>Kontakt</H>
    <p>
      E-Mail:{" "}
      <a className="underline" href="mailto:info@naijacashews.com">
        info@naijacashews.com
      </a>
    </p>

    <H>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</H>
    <p>Neal Chidi Emegwele</p>

    <H>Disclaimer</H>
    <p>
      This website is currently a pre-launch brand website. No products are
      currently offered for sale through this website.
    </p>
  </LegalPage>
);

export const Imprint = () => (
  <LegalPage title="Imprint" lastUpdated="December 2025">
    <H>Company Information</H>
    <p>
      NAIJA Cashews (concept brand preview)
      <br />
      Berlin, Germany
    </p>
    <H>Founder & Responsible for Content</H>
    <p>
      Neal Chidi Emegwele
      <br />
      Email: <a className="underline" href="mailto:info@naijacashews.com">info@naijacashews.com</a>
      <br />
      Website: www.naijacashews.com
    </p>
    <H>Departments</H>
    <p>
      Sales: <a className="underline" href="mailto:sales@naijacashews.com">sales@naijacashews.com</a>
      <br />
      Marketing: <a className="underline" href="mailto:marketing@naijacashews.com">marketing@naijacashews.com</a>
    </p>
    <H>Liability for Content</H>
    <p>
      As a service provider, we are responsible for our own content on these
      pages according to applicable law. We are not, however, obliged to
      monitor third-party information transmitted or stored.
    </p>
    <H>Liability for Links</H>
    <p>
      Our website contains links to third-party websites whose content we
      cannot influence. We therefore cannot assume any liability for such
      external content.
    </p>
    <H>Copyright</H>
    <p>
      All content and works on these pages created by NAIJA Cashews are
      subject to copyright. Reproduction, processing, distribution, or any
      form of commercialization beyond the scope of copyright law requires
      the prior written consent of NAIJA Cashews.
    </p>
  </LegalPage>
);

export const Privacy = () => (
  <LegalPage title="Privacy Policy" lastUpdated="December 2025">
    <H>Overview</H>
    <p>
      We respect your privacy. This policy explains what data we collect when
      you visit naijacashews.com or join our launch list, why we collect it,
      and how we protect it.
    </p>
    <H>Data We Collect</H>
    <ul className="list-disc pl-6 space-y-2">
      <li>Launch-list signups: your name and email address.</li>
      <li>Contact messages: name, email and the content of your message.</li>
      <li>
        Basic, anonymous traffic analytics to understand how the site is used.
      </li>
    </ul>
    <H>How We Use Your Data</H>
    <p>
      We use your email solely to send you launch updates and respond to your
      enquiries. We do not sell, rent or trade your personal information.
    </p>
    <H>Data Storage</H>
    <p>
      Signup data is stored in a secure database. Access is limited to
      authorised members of the NAIJA Cashews team.
    </p>
    <H>Your Rights</H>
    <p>
      You can request access, correction or deletion of your data at any time
      by writing to <a className="underline" href="mailto:info@naijacashews.com">info@naijacashews.com</a>.
    </p>
    <H>Cookies</H>
    <p>
      We use only essential cookies required for the site to function. We do
      not use third-party advertising cookies.
    </p>
  </LegalPage>
);

export const Terms = () => (
  <LegalPage title="Terms & Conditions" lastUpdated="December 2025">
    <H>Acceptance</H>
    <p>
      By using naijacashews.com, you agree to these terms. If you do not
      agree, please do not use the site.
    </p>
    <H>Concept Preview</H>
    <p>
      This website is currently a concept brand preview. Product availability,
      pricing and launch dates are indicative and may change before the
      official release.
    </p>
    <H>Launch List</H>
    <p>
      Joining the launch list does not constitute a binding order. Early
      access and founder pricing are offered at the discretion of NAIJA
      Cashews and may be subject to limited availability.
    </p>
    <H>Intellectual Property</H>
    <p>
      All trademarks, logos and content displayed on this site are the
      property of NAIJA Cashews unless otherwise indicated. You may not
      reproduce them without prior written consent.
    </p>
    <H>Limitation of Liability</H>
    <p>
      To the maximum extent permitted by law, NAIJA Cashews shall not be
      liable for any indirect, incidental or consequential damages arising
      from your use of the site.
    </p>
    <H>Governing Law</H>
    <p>
      These terms are governed by the laws of the Federal Republic of
      Germany. Place of jurisdiction is Berlin.
    </p>
    <H>Contact</H>
    <p>
      Questions about these terms? Write to{" "}
      <a className="underline" href="mailto:info@naijacashews.com">info@naijacashews.com</a>.
    </p>
  </LegalPage>
);
